import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShieldCheck, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { API } from '@/App';
import { useLanguage } from '@/i18n/LanguageContext';
import PasswordInput from '@/components/PasswordInput';

const t = {
  en: { title: "Reset Password", newPassword: "New Password (min 12 characters)", confirm: "Confirm Password", submit: "Reset Password", resetting: "Resetting...", success: "Password reset successfully. You can now log in.", backToLogin: "Back to login", expired: "This reset link is invalid or expired." },
  sk: { title: "Obnova hesla", newPassword: "Nov\u00E9 heslo (min 12 znakov)", confirm: "Potvrdi\u0165 heslo", submit: "Obnovi\u0165 heslo", resetting: "Obnovuje sa...", success: "Heslo bolo \u00FAspe\u0161ne obnoven\u00E9. M\u00F4\u017Eete sa prihl\u00E1si\u0165.", backToLogin: "Sp\u00E4\u0165 na prihl\u00E1senie", expired: "Tento odkaz na obnovu je neplatn\u00FD alebo expirovan\u00FD." },
  de: { title: "Passwort zur\u00FCcksetzen", newPassword: "Neues Passwort (min. 12 Zeichen)", confirm: "Passwort best\u00E4tigen", submit: "Passwort zur\u00FCcksetzen", resetting: "Wird zur\u00FCckgesetzt...", success: "Passwort erfolgreich zur\u00FCckgesetzt. Sie k\u00F6nnen sich jetzt anmelden.", backToLogin: "Zur\u00FCck zur Anmeldung", expired: "Dieser Link ist ung\u00FCltig oder abgelaufen." }
};

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { language } = useLanguage();
  const copy = t[language] || t.en;
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0e0b04] flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-[#cba135]/70 mb-4">{copy.expired}</p>
          <Link to="/" className="text-[#cba135] underline">{copy.backToLogin}</Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 12) { toast.error(copy.newPassword); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword, confirmPassword })
      });
      const data = await res.json();
      if (res.ok) {
        setDone(true);
        toast.success(copy.success);
      } else {
        toast.error(data.detail || copy.expired);
      }
    } catch { toast.error('Network error'); }
    setLoading(false);
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#0e0b04] flex items-center justify-center p-6">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          className="text-center space-y-4">
          <CheckCircle size={48} weight="duotone" className="text-[#cba135] mx-auto" />
          <p className="text-[#cba135]">{copy.success}</p>
          <Link to="/" className="inline-block mt-4 text-[#cba135] underline underline-offset-4">{copy.backToLogin}</Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0e0b04] flex items-center justify-center p-6">
      <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="w-full max-w-md bg-gradient-to-br from-[#1a1508]/80 to-[#0e0b04]/80 border border-[#cba135]/10 rounded-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck size={28} weight="duotone" className="text-[#cba135]" />
          <h1 className="heading-font text-2xl text-[#cba135]">{copy.title}</h1>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-[#cba135]/70 text-sm">{copy.newPassword}</Label>
            <PasswordInput data-testid="reset-new-password" value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)} required minLength={12}
              className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135]" />
          </div>
          <div>
            <Label className="text-[#cba135]/70 text-sm">{copy.confirm}</Label>
            <PasswordInput data-testid="reset-confirm-password" value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)} required minLength={12}
              className="bg-[#1a1508] border-[#cba135]/20 text-[#cba135]" />
          </div>
          <Button data-testid="reset-submit-button" type="submit" disabled={loading}
            className="w-full bg-gradient-to-r from-[#cba135] to-[#a8872b] text-[#0e0b04] font-bold">
            {loading ? copy.resetting : copy.submit}
          </Button>
        </form>
        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-[#cba135]/70 hover:text-[#cba135]">{copy.backToLogin}</Link>
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
